import { motion } from "framer-motion";
import { BookOpenCheck, LineChart, FileCheck, PiggyBank, RefreshCw, Brain } from "lucide-react";

const painPoints = [
  {
    title: "Complex Regulations",
    description: "Staying compliant with changing UK/EU rules drains resources and slows teams down.",
    icon: BookOpenCheck,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    title: "Inefficient Tracking",
    description: "Manual systems are error-prone and expensive — creating hidden compliance risks.",
    icon: LineChart,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    title: "Contractor Demands",
    description: "Large builders now expect verified sustainability data. Spreadsheets can't deliver.",
    icon: FileCheck,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    title: "Expensive Custom Builds",
    description: "Building your own compliance system could cost thousands, take months and risks failure.",
    icon: PiggyBank,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    title: "Outdated Tools",
    description: "Traditional methods just can't keep pace with today's fast-moving, data-driven environment.",
    icon: RefreshCw,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    title: "Team Strain",
    description: "Compliance tasks pull staff away from core operations, lowering productivity and morale.",
    icon: Brain,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
];

export function PainPointsSection() {
  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold relative mb-2 sm:mb-4 pb-1 leading-tight">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#00DC82]/20 to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="text-foreground">Navigating the </span>
            <span className="text-[#00DC82]">Compliance Maze</span>
          </h2>
          <div className="flex justify-center">
            <p className="text-base sm:text-lg md:text-xl leading-relaxed tracking-tight text-muted-foreground max-w-4xl text-center mt-2 md:mt-4 lg:mt-6">
              Sentiri helps construction product manufacturers overcome the growing burden of compliance and material tracking - streamlining processes, saving time and unlocking competitive advantages.
            </p>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {painPoints.map((point, index) => (
            <motion.div 
              key={point.title}
              className="group relative bg-card rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 border-[1.5px] border-[#00DC82]/10 hover:border-[#00DC82] hover:shadow-[#00DC82]/30 hover:shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`absolute inset-0 opacity-[0.03] bg-gradient-to-br ${point.gradient}`} />
              
              <div className="relative p-4 sm:p-5 md:p-6">
                <div className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-[#00DC82] group-hover:scale-110 transition-transform duration-500">
                  <point.icon className="w-full h-full" />
                </div>
                
                <h3 className="text-xl sm:text-2xl font-semibold text-card-foreground mb-2 md:mb-3 group-hover:text-[#00DC82] transition-colors duration-300">
                  {point.title}
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                  {point.description}
                </p>

                <div className="absolute inset-0 border-[1.5px] border-transparent rounded-xl transition-all duration-500 group-hover:border-[#00DC82] group-hover:border-opacity-20" />
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.p 
          className="text-center mt-10 text-base md:text-lg font-medium"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {/* <span className="text-[#00DC82]">Sentiri addresses these challenges</span>, streamlining compliance and material tracking effortlessly. */}
        </motion.p>
      </div>
    </div>
  );
}
